/**
 * notifyHost.js — одно место, где живут все нижние уведомления.
 *
 * Зачем. На снимке экрана у врача одновременно оказались: зелёный чип объёма,
 * красная полоса и синий тост «Собираю RTSTRUCT…» — все друг на друге. Причина
 * не в каком-то одном модуле, а в том, что таких модулей семь, и каждый сам
 * ставил себе position:fixed в правый нижний угол со своим z-index — от 10001
 * до 2147480000. Кто последний, тот и сверху.
 *
 * Хуже, чем некрасиво: под этой кучей потерялось сообщение об ошибке экспорта.
 * Врач видел «Собираю RTSTRUCT…» и думал, что программа думает, — а она уже
 * час как сообщила, что не смогла.
 *
 * Решение — не подбирать z-index (это война, в которой побеждает последний
 * правивший файл), а убрать саму возможность наложения: есть один контейнер,
 * он раскладывает содержимое в столбец, и перекрыть друг друга элементы просто
 * не могут.
 *
 * Порядок сверху вниз: важное ниже, ближе к большому пальцу и к взгляду.
 */

const HOST_ID = 'mikhmel-notify';

/** Чем ниже число, тем выше в столбце. Важное — внизу, под рукой. */
export const SLOT = {
  INFO: 10,      // тосты, подсказки
  RESULT: 20,    // результат измерения — держится долго
  ACTION: 30,    // требует решения: восстановить сеанс, открыть заново
  ALERT: 40,     // случилось плохое
};

export function getNotifyHost() {
  let host = document.getElementById(HOST_ID);
  if (host) return host;
  host = document.createElement('div');
  host.id = HOST_ID;
  // [10.0.2] Столбец уведомлений поднят над мобильной навигацией.
  //
  // Было bottom:10px при z-index 2147480000. Нижняя панель #mobile-nav стоит
  // на bottom:0 высотой calc(var(--mobile-nav-h) + var(--safe-bottom)) с
  // z-index:1000 — то есть уведомления ложились ровно на ряд кнопок и
  // накрывали его целиком: ширина min(94vw,560px) на телефоне 390px даёт
  // 366px, это вся полоса, а не угол.
  //
  // Отступ считается тем же выражением, что у .mobile-drawer (index.html),
  // чтобы не разъехалось при смене высоты панели или в ландшафте, где
  // --mobile-nav-h становится 48px. На десктопе переменные не заданы, и
  // fallback возвращает прежние 10px.
  host.setAttribute('style',
    'position:fixed;right:10px;left:auto;z-index:2147480000;'
    + 'bottom:calc(10px + var(--mobile-nav-h, 0px) + var(--safe-bottom, 0px));'
    + 'width:min(94vw,560px);display:flex;flex-direction:column;gap:8px;'
    + 'align-items:stretch;pointer-events:none;max-height:70vh;overflow:hidden auto;');
  document.body.appendChild(host);
  return host;
}

// ── свёрнутый вид на телефоне ───────────────────────────────────────────────
//
// [10.0.2] Карточка, которая ждёт решения, на телефоне занимает пол-экрана.
// Поднять её над нижней панелью мало: она всё равно закрывает изображение,
// только уже другое место. А решение человек примет не сейчас, а когда
// досмотрит серию.
//
// Поэтому свёрнутый вид — не полоса в общем столбце, а МАЛЕНЬКАЯ КРУГЛАЯ
// КНОПКА, висящая поверх снимка отдельно от столбца уведомлений. Её можно
// перетащить куда угодно пальцем; место запоминается. Пока она свёрнута,
// столбец уведомлений не занимает высоты вовсе — нижний ряд кнопок свободен.
//
// Сворачиваем только то, что требует решения (ACTION и выше). Тост «сохранено»
// живёт три секунды и сам уходит — прятать его за кнопку значит заставить
// человека нажимать, чтобы прочитать то, чего он не просил.

const NARROW_PX = 768;
const DOCK_ID = 'mikhmel-notify-dock';
const DOCK_POS_KEY = 'mikhmel.noticeDockPos';
const BTN_SIZE = 44;
const DRAG_SLOP = 6;      // меньше — случайное дрожание пальца принималось бы за перенос

function _isNarrow() {
  try { return window.matchMedia(`(max-width: ${NARROW_PX}px)`).matches; }
  catch (e) { return false; }
}

/** Короткая суть для подписи кнопки: явная подпись, заголовок карточки или текст. */
function _shortLabel(el) {
  let t = '';
  try {
    t = el.dataset?.noticeLabel
      || el.querySelector('b, strong, [data-notice-title]')?.textContent
      || el.textContent
      || '';
  } catch (e) { t = ''; }
  t = String(t).replace(/\s+/g, ' ').trim();
  if (!t) return 'Сообщение';
  return t.length > 60 ? `${t.slice(0, 59)}…` : t;
}

// ── плавающая кнопка: место ────────────────────────────────────────────────

function _clampDock(x, y, count = 1) {
  const w = window.innerWidth || 360;
  const h = window.innerHeight || 640;
  const dockH = count * BTN_SIZE + (count - 1) * 8;
  return {
    x: Math.max(4, Math.min(x, w - BTN_SIZE - 4)),
    y: Math.max(4, Math.min(y, h - dockH - 4)),
  };
}

function _loadDockPos() {
  try {
    const raw = JSON.parse(localStorage.getItem(DOCK_POS_KEY) || 'null');
    if (raw && Number.isFinite(raw.x) && Number.isFinite(raw.y)) return raw;
  } catch (e) { /* хранилище закрыто — встанем по умолчанию */ }
  // По умолчанию — правый край, чуть выше середины: и от нижней панели далеко,
  // и большим пальцем достаётся.
  return { x: (window.innerWidth || 360) - BTN_SIZE - 8, y: Math.round((window.innerHeight || 640) * 0.42) };
}

function _saveDockPos(pos) {
  try { localStorage.setItem(DOCK_POS_KEY, JSON.stringify(pos)); } catch (e) { /* */ }
}

function _placeDock(dock, pos) {
  const p = _clampDock(pos.x, pos.y, Math.max(1, dock.childElementCount));
  dock.style.left = `${p.x}px`;
  dock.style.top = `${p.y}px`;
  return p;
}

/**
 * Перетаскивание. Нажатие без движения остаётся нажатием: иначе кнопка,
 * которую нельзя случайно сдвинуть, не открывалась бы вовсе.
 */
function _makeDraggable(dock) {
  let startX = 0, startY = 0, baseX = 0, baseY = 0, active = false;

  const onMove = (e) => {
    if (!active) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (dock.dataset.dragging !== '1' && Math.hypot(dx, dy) < DRAG_SLOP) return;
    dock.dataset.dragging = '1';
    e.preventDefault();
    _placeDock(dock, { x: baseX + dx, y: baseY + dy });
  };

  const onUp = () => {
    if (!active) return;
    active = false;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    if (dock.dataset.dragging === '1') {
      _saveDockPos({ x: parseFloat(dock.style.left) || 0, y: parseFloat(dock.style.top) || 0 });
      // Флаг снимаем на следующем кадре: click прилетает после pointerup, и
      // без задержки перенос кнопки заканчивался бы её открытием.
      setTimeout(() => { dock.dataset.dragging = '0'; }, 0);
    }
  };

  dock.addEventListener('pointerdown', (e) => {
    active = true;
    dock.dataset.dragging = '0';
    startX = e.clientX; startY = e.clientY;
    baseX = parseFloat(dock.style.left) || 0;
    baseY = parseFloat(dock.style.top) || 0;
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  });

  // Поворот экрана и смена клавиатуры меняют границы — кнопка не должна
  // остаться за краем.
  const onResize = () => {
    _placeDock(dock, { x: parseFloat(dock.style.left) || 0, y: parseFloat(dock.style.top) || 0 });
  };
  window.addEventListener('resize', onResize);
  // Док живёт, пока есть хоть одна свёрнутая карточка; когда последняя ушла,
  // его убирают — и слушатель размера обязан уйти с ним, иначе за сеанс
  // накопится по одному на каждое уведомление.
  dock._cleanup = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    window.removeEventListener('resize', onResize);
  };
}

function _getDock() {
  let dock = document.getElementById(DOCK_ID);
  if (dock) return dock;
  dock = document.createElement('div');
  dock.id = DOCK_ID;
  dock.dataset.dragging = '0';
  dock.setAttribute('style',
    'position:fixed;z-index:2147480001;display:flex;flex-direction:column;gap:8px;'
    + 'align-items:center;touch-action:none;');
  _placeDock(dock, _loadDockPos());
  document.body.appendChild(dock);
  _makeDraggable(dock);
  return dock;
}

/** Пустой док убираем: невидимый слой всё равно ловит касания. */
function _syncDock() {
  const dock = document.getElementById(DOCK_ID);
  if (dock && !dock.childElementCount) {
    try { dock._cleanup?.(); } catch (e) { /* */ }
    dock.remove();
  }
}

const BTN_CSS = `width:${BTN_SIZE}px;height:${BTN_SIZE}px;flex:0 0 auto;border-radius:50%;`
  + 'display:flex;align-items:center;justify-content:center;'
  + 'background:#12243a;color:#e6eefb;border:1px solid #3a6ea5;'
  + 'font:700 18px/1 system-ui,sans-serif;cursor:pointer;padding:0;'
  + 'box-shadow:0 6px 20px rgba(0,0,0,.55);touch-action:none;';

/**
 * Обернуть карточку в пару «плавающая кнопка ↔ карточка».
 * @returns {HTMLElement} обёртка, которую кладём в столбец вместо карточки
 */
function _collapsible(el, slot) {
  const wrap = document.createElement('div');
  wrap.dataset.slot = String(slot);
  wrap.dataset.collapsed = '1';
  wrap.style.cssText = 'display:flex;flex-direction:column;align-items:stretch;gap:6px;pointer-events:none;';

  const label = _shortLabel(el);
  const dock = _getDock();

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', label);
  btn.title = label;
  btn.style.cssText = BTN_CSS;
  // Значок по важности: тревога — восклицательный знак, требуется решение —
  // стрелка возврата. Текст на 44 пикселях не читается, а подпись есть в title.
  btn.textContent = slot >= SLOT.ALERT ? '!' : '↻';

  const fold = document.createElement('button');
  fold.type = 'button';
  fold.textContent = 'Свернуть';
  fold.style.cssText = 'pointer-events:auto;align-self:flex-end;background:transparent;'
    + 'border:1px solid #456;color:#bcd;border-radius:8px;padding:6px 12px;'
    + 'font:12px/1 system-ui,sans-serif;cursor:pointer;';

  const setOpen = (open) => {
    wrap.dataset.collapsed = open ? '0' : '1';
    // Свёрнутая карточка не занимает места в столбце вовсе — иначе она снова
    // отжимала бы нижний ряд кнопок, ради чего всё и затевалось.
    wrap.style.display = open ? 'flex' : 'none';
    btn.style.display = open ? 'none' : 'flex';
    fold.style.display = open ? 'block' : 'none';
    el.style.display = open ? '' : 'none';
    btn.setAttribute('aria-expanded', String(open));
  };

  btn.addEventListener('click', () => {
    if (dock.dataset.dragging === '1') return;   // это был перенос, а не нажатие
    setOpen(true);
  });
  fold.addEventListener('click', () => setOpen(false));

  wrap.appendChild(el);
  wrap.appendChild(fold);
  dock.appendChild(btn);
  setOpen(false);

  // Карточку убирает её собственная кнопка «Закрыть» — она про обёртку и
  // кнопку не знает и знать не должна. Убираем и то и другое следом, иначе
  // на экране осталась бы кнопка, за которой ничего нет.
  try {
    const mo = new MutationObserver(() => {
      if (!el.isConnected || el.parentNode !== wrap || !wrap.isConnected) {
        mo.disconnect(); btn.remove(); wrap.remove(); _syncDock();
      }
    });
    mo.observe(wrap, { childList: true });
    // Второй адрес наблюдения — сам столбец: обёртку может убрать не только
    // карточка изнутри, но и уборка снаружи, а кнопка тогда осталась бы висеть.
    mo.observe(getNotifyHost(), { childList: true });
  } catch (e) { /* наблюдателя нет — обёртка уйдёт вместе с _trim */ }

  return wrap;
}

/**
 * Поместить элемент в общий столбец.
 *
 * Собственное позиционирование у элемента снимается: пока он остаётся
 * position:fixed, он живёт по своим правилам и снова кого-нибудь перекроет.
 *
 * @param {HTMLElement} el
 * @param {number} [slot=SLOT.INFO] — из SLOT
 */
export function mountNotice(el, slot = SLOT.INFO) {
  if (!el) return el;
  try {
    const host = getNotifyHost();
    el.dataset.slot = String(slot);
    el.style.position = 'static';
    el.style.left = ''; el.style.right = ''; el.style.bottom = ''; el.style.top = '';
    el.style.zIndex = '';
    // [10.19.1] Нажатия ловит только то, что их ждёт.
    //
    // Здесь стояло безусловное pointer-events:auto — и столбец уведомлений
    // ловил нажатия ВСЕЙ своей шириной (min(94vw,560px), на телефоне это почти
    // весь экран), даже когда в нём висел один тост «сохранено». Осмотр кнопок
    // на телефоне 412×915 в режиме сравнения показал: кнопки масштаба окна, ✕
    // карточки выбора и «Карта разницы» на время показа тоста становились
    // глухими. Тост нажатий не ждёт (обработчика у него нет, гаснет по
    // времени) — а карточка, требующая решения, ждёт, и ей auto остаётся.
    el.style.pointerEvents = (slot > SLOT.INFO) ? 'auto' : 'none';
    if (!el.style.width) el.style.width = 'auto';

    const node = (slot >= SLOT.ACTION && _isNarrow()) ? _collapsible(el, slot) : el;

    // Вставляем по порядку важности, а не в конец: иначе тост, пришедший
    // позже, оттеснил бы вниз то, что требует решения.
    const kids = [...host.children];
    const after = kids.find(k => Number(k.dataset.slot || 0) > slot);
    if (after) host.insertBefore(node, after); else host.appendChild(node);

    _trim(host);
  } catch (e) { /* показать важнее, чем разложить */ }
  return el;
}

/**
 * Не давать столбцу разрастись. Убираем самые верхние и наименее важные —
 * то есть старые информационные, а не то, что ждёт решения.
 */
function _trim(host, max = 5) {
  const kids = [...host.children];
  if (kids.length <= max) return;
  const removable = kids
    .filter(k => Number(k.dataset.slot || 0) <= SLOT.RESULT)
    .slice(0, kids.length - max);
  for (const k of removable) k.remove();
}

/** Убрать всё, что не требует решения (например, при закрытии исследования). */
export function clearNotices() {
  const host = document.getElementById(HOST_ID);
  if (!host) return;
  for (const k of [...host.children]) {
    if (Number(k.dataset.slot || 0) < SLOT.ACTION) k.remove();
  }
}

export default { getNotifyHost, mountNotice, clearNotices, SLOT };
